import { useCallback, useEffect } from 'react';
import Avatar from 'react-avatar';

import { generateAvatar } from '@apiCalls';
import { InputField, PhoneField } from '@components';
import { useAppContext } from '@contexts';
import { formatMobile, isValidEmail } from '@utils';
import {
  handleRenderError,
  withErrorBoundary,
  withErrorHandler,
} from '@utils/errorHandler';

interface IContactDetailsFormProperties {
  attendeeDetails: IAttendee;
  avatarString: string;
  billingDetails?: IBillingDetails | null;
  onSubmit?: (event: React.FormEvent) => void;
  setAttendeeDetails: (attendeeDetails: IAttendee) => void;
  setAvatarString: (avatarString: string) => void;
  setBlocked: (blocked: boolean) => void;
  setUseManualEntry: (useManualEntry: boolean) => void;
  useManualEntry: boolean;
}

export const ContactDetailsForm = ({
  attendeeDetails,
  avatarString,
  billingDetails,
  onSubmit,
  setAttendeeDetails,
  setAvatarString,
  setBlocked,
  setUseManualEntry,
  useManualEntry,
}: IContactDetailsFormProperties) => {
  try {
    const { event, eventSeries } = useAppContext();
    const fullName = billingDetails
      ? `${billingDetails.FirstName} ${billingDetails.LastName}`
      : '';

    const checkFormEntriesValid = useCallback(
      withErrorHandler(() => {
        if (billingDetails && !useManualEntry) {
          return setBlocked(false);
        }
        if (
          attendeeDetails.FirstName?.trim() === '' ||
          attendeeDetails.LastName?.trim() === '' ||
          !isValidEmail(attendeeDetails.Email) ||
          attendeeDetails.Phone?.trim() === ''
        ) {
          return setBlocked(true);
        }
        return setBlocked(false);
      }),
      [attendeeDetails, billingDetails, setBlocked, useManualEntry]
    );

    useEffect(
      withErrorBoundary(() => {
        if (billingDetails && event) {
          setUseManualEntry(false);
          // Use existing Photo from billingDetails if available (already generated in billing.tsx)
          if (billingDetails.Photo) {
            setAvatarString(billingDetails.Photo);
          } else {
            // Fallback: generate avatar only if Photo doesn't exist
            const { FirstName: firstName, LastName: lastName } = billingDetails;
            generateAvatar({
              FirstName: firstName,
              LastName: lastName,
              event,
            }).then(avatar => {
              if (avatar) {
                setAvatarString(avatar);
              }
            });
          }
        }
      }),
      [billingDetails, event, setAvatarString, setUseManualEntry]
    );

    useEffect(() => {
      checkFormEntriesValid();
    }, [checkFormEntriesValid]);

    return (
      <div style={{ height: '100%' }}>
        {billingDetails && (
          <>
            <div
              className={`checkoutAttendeeDetailsOption ${
                useManualEntry ? '' : 'selected'
              }`}
              id='attendeeDetailsAutomatic'
              onClick={() => setUseManualEntry(false)}
            >
              <div className='checkoutAttendeeDetailsOptionCheck'>
                <svg
                  className={`bulletCheck ${useManualEntry ? '' : 'checked'}`}
                  data-name='bulletCheck'
                  xmlns='http://www.w3.org/2000/svg'
                  viewBox='0 0 22 22'
                >
                  <path
                    className='bullet'
                    d='M11,22A11,11,0,1,1,22,11,10.96832,10.96832,0,0,1,11,22ZM11,2a9,9,0,1,0,9,9A8.96258,8.96258,0,0,0,11,2Z'
                  />
                  <circle className='bulletChecked' cx='11' cy='11' r='5' />
                </svg>
              </div>
              <div className='checkoutAttendeeDetailsOptionText'>
                <div className='checkoutAttendeeDetailsOptionName'>
                  <h2>Automatically Use Billing Details</h2>
                </div>
                <h4 className='checkoutAttendeeDetailsOptionDescription'>
                  Recommended
                </h4>
              </div>
            </div>
            <div
              className={`attendeeProfileContainer ${
                useManualEntry ? 'hidden' : ''
              }`}
            >
              <div className='attendeeProfilePhoto'>
                {avatarString ? (
                  <img
                    src={`data:image/png;base64,${avatarString}`}
                    alt='avatar'
                  />
                ) : (
                  <Avatar
                    name={fullName}
                    color={
                      event?.ColourSecondary ||
                      eventSeries?.ColourSecondary ||
                      ''
                    }
                    fgColor={
                      event?.ColourBackground ||
                      eventSeries?.ColourBackground ||
                      ''
                    }
                    size='40'
                  />
                )}
              </div>
              <div className='attendeeProfileDetails'>
                <h2 className='attendeeDetailsFullName'>{fullName}</h2>
                <h4 className='attendeeDetailsEmail'>{billingDetails.Email}</h4>
                <h4 className='attendeeDetailsPhone'>
                  {formatMobile(billingDetails.Phone)}
                </h4>
              </div>
            </div>
          </>
        )}
        <div
          className={`checkoutAttendeeDetailsOption ${
            useManualEntry ? 'selected' : ''
          }`}
          id='attendeeDetailsManual'
          onClick={() => setUseManualEntry(true)}
          style={billingDetails ? {} : { display: 'none' }}
        >
          <div className='checkoutAttendeeDetailsOptionCheck'>
            <svg
              className={`bulletCheck ${useManualEntry ? 'checked' : ''}`}
              data-name='bulletCheck'
              xmlns='http://www.w3.org/2000/svg'
              viewBox='0 0 22 22'
            >
              <path
                className='bullet'
                d='M11,22A11,11,0,1,1,22,11,10.96832,10.96832,0,0,1,11,22ZM11,2a9,9,0,1,0,9,9A8.96258,8.96258,0,0,0,11,2Z'
              />
              <circle className='bulletChecked' cx='11' cy='11' r='5' />
            </svg>
          </div>
          <div className='checkoutAttendeeDetailsOptionText'>
            <div className='checkoutAttendeeDetailsOptionName'>
              <h2>Manually Add Details</h2>
            </div>
          </div>
        </div>

        <form
          className={`formContactDetails ${useManualEntry ? '' : 'hidden'}`}
          onSubmit={onSubmit}
        >
          <div className='form-row form-row-double' id='addAttendeeNameRow'>
            <InputField
              type='text'
              id='firstName'
              name='firstName'
              label='First Name'
              value={attendeeDetails.FirstName}
              onChange={uiEvent => {
                const target = uiEvent.target as HTMLTextAreaElement;
                setAttendeeDetails({
                  ...attendeeDetails,
                  FirstName: target?.value ?? '',
                });
              }}
              required
            />
            <InputField
              type='text'
              id='lastName'
              name='lastName'
              label='Last Name'
              value={attendeeDetails.LastName}
              onChange={uiEvent => {
                const target = uiEvent.target as HTMLTextAreaElement;
                setAttendeeDetails({
                  ...attendeeDetails,
                  LastName: target?.value ?? '',
                });
              }}
              required
            />
          </div>
          <div className='form-row' id='addAttendeeEmailRow'>
            <InputField
              type='email'
              id='email'
              name='email'
              label='Email'
              value={attendeeDetails.Email}
              onChange={uiEvent => {
                const target = uiEvent.target as HTMLTextAreaElement;
                setAttendeeDetails({
                  ...attendeeDetails,
                  Email: target?.value ?? '',
                });
              }}
              required
            />
          </div>
          <div className='form-row form-row-double' id='addAttendeePhoneRow'>
            <PhoneField
              id='phone'
              name='phone'
              label='Phone'
              value={attendeeDetails.Phone}
              onChange={Phone => {
                setAttendeeDetails({
                  ...attendeeDetails,
                  Phone,
                });
              }}
            />
          </div>
          <input type='submit' hidden />
        </form>
      </div>
    );
  } catch (error) {
    return handleRenderError(error);
  }
};
